// ============================================================
// DM-LMS — useApi Hook
// DRY: one fetch/mutation helper for all role pages, following
// the same raw-fetch + envelope-unwrap pattern as useAuth.
// ============================================================

import { useCallback, useEffect, useRef, useState } from "react";
import type { ApiResponse } from "@dm-lms/shared/types";
import { devRoleHeaders } from "./devRole.js";

// Absolute in staging/production (web and API are on different origins);
// empty in local dev, where Vite's dev server proxies /api to the Worker.
export const API_URL = import.meta.env.VITE_API_URL ?? "";

interface FetchState<T> {
  data:      T | null;
  isLoading: boolean;
  error:     string | null;
}

interface PollOptions<T> {
  pollMs:     number;
  pollWhile?: (data: T) => boolean;   // keep polling only while this holds for the latest data
}

// Pass `url: null` to skip fetching (e.g. until a dependent selection is made).
// `refetch` reloads with a loading state; `refresh` (and polling) update data in place, silently.
export function useFetch<T>(
  url: string | null,
  poll?: PollOptions<T>
): FetchState<T> & { refetch: () => void; refresh: () => Promise<void> } {
  const [state, setState] = useState<FetchState<T>>({ data: null, isLoading: url !== null, error: null });
  const [tick, setTick] = useState(0);
  const currentUrl = useRef(url);
  currentUrl.current = url;

  useEffect(() => {
    if (!url) {
      setState({ data: null, isLoading: false, error: null });
      return;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    async function run() {
      try {
        const res = await fetch(`${API_URL}${url}`, { credentials: "include", headers: devRoleHeaders() });
        const json = (await res.json()) as ApiResponse<T>;
        if (cancelled) return;
        if (!json.ok) {
          setState({ data: null, isLoading: false, error: json.error });
          return;
        }
        setState({ data: json.data, isLoading: false, error: null });
      } catch {
        if (!cancelled) setState({ data: null, isLoading: false, error: "Network error" });
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [url, tick]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  // No loading flash: the page keeps whatever the user is typing into or has expanded.
  // A failed refresh keeps the last good data; the next poll tick simply tries again.
  // Resolves once the new data is applied (never rejects), so a caller can wait for it.
  const refresh = useCallback(async () => {
    if (!url) return;
    try {
      const res = await getJson<T>(url);
      if (res.ok && currentUrl.current === url) setState((prev) => ({ ...prev, data: res.data, error: null }));
    } catch {
      // keep the last good data
    }
  }, [url]);

  const pollMs = poll?.pollMs;
  const shouldPoll = pollMs !== undefined && state.data !== null && (poll?.pollWhile?.(state.data) ?? true);
  useEffect(() => {
    if (!shouldPoll || pollMs === undefined) return;
    const timer = setInterval(() => { void refresh(); }, pollMs);
    return () => {
      clearInterval(timer);
    };
  }, [shouldPoll, pollMs, refresh]);

  return { ...state, refetch, refresh };
}

export async function getJson<T>(url: string): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_URL}${url}`, { credentials: "include", headers: devRoleHeaders() });
  return (await res.json()) as ApiResponse<T>;
}

export async function postJson<T>(url: string, body?: unknown): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_URL}${url}`, {
    method:      "POST",
    credentials: "include",
    headers:     { "Content-Type": "application/json", ...devRoleHeaders() },
    body:        JSON.stringify(body ?? {}),
  });
  return (await res.json()) as ApiResponse<T>;
}

export async function patchJson<T>(url: string, body: unknown): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_URL}${url}`, {
    method:      "PATCH",
    credentials: "include",
    headers:     { "Content-Type": "application/json", ...devRoleHeaders() },
    body:        JSON.stringify(body),
  });
  return (await res.json()) as ApiResponse<T>;
}

export async function deleteJson<T>(url: string): Promise<ApiResponse<T>> {
  const res = await fetch(`${API_URL}${url}`, {
    method:      "DELETE",
    credentials: "include",
    headers:     devRoleHeaders(),
  });
  return (await res.json()) as ApiResponse<T>;
}
